import Cookies from 'js-cookie';
import {
  createRouter, createWebHistory, NavigationGuardNext,
  RouteLocationNormalized,
} from 'vue-router';
import DDLogs from './DDLogs';
import Segment from './Segment';
import { isLoggedIn } from './api/loginApi';
import AsyncLoadingWrapper from './components/Common/AsyncLoadingWrapper.vue';
import ErrorBoundary from './components/Common/ErrorBoundary.vue';
import GoToApp from './components/Common/GoToApp.vue';
import ReferToDownloadApp from './components/Common/ReferToDownloadApp.vue';
import DescopeEtoroOtsSignup from './components/descope/DescopeEtoroOtsSignup.vue';
import DescopeLogin from './components/descope/DescopeLogin.vue';
import DescopeLogout from './components/descope/DescopeLogout.vue';
import DescopeMemberInvite from './components/descope/DescopeMemberInvite.vue';
import DescopeMobileLogin from './components/descope/DescopeMobileLogin.vue';
import DescopeMobileTestLogin from './components/descope/DescopeMobileTestLogin.vue';
import DescopeProgradOtsSignup from './components/descope/DescopeProgradOtsSignup.vue';
import DescopeSalaryFinanceOtsSignup from './components/descope/DescopeSalaryFinanceOtsSignup.vue';
import DescopeRiseupWLOtsSignup from './components/descope/DescopeRiseupWLOtsSignup.vue';
import DescopeSignup from './components/descope/DescopeSignup.vue';
import DescopeSyndesiOtsSignup from './components/descope/DescopeSyndesiOtsSignup.vue';
import DescopeTestLogin from './components/descope/DescopeTestLogin.vue';
import DescopeTestSignup from './components/descope/DescopeTestSignup.vue';
import JoinRiseup from './components/member-join/JoinRiseup.vue';
import WelcomeNewMember from './components/member-join/WelcomeNewMember.vue';
import OtsCustomSuccess from './components/ots/OtsCustomSuccess.vue';
import OtsStoryConnect from './components/ots/OtsStoryConnect.vue';
import OtsStorySignup from './components/ots/OtsStorySignup.vue';
import OtsStorySuccess from './components/ots/OtsStorySuccess.vue';
import OtsVanillaSuccess from './components/ots/OtsVanillaSuccess.vue';
import ProgradConnectOtsAccount from './components/ots/Prograd/ProgradConnectOtsAccount.vue';
import ProgradSuccess from './components/ots/Prograd/ProgradSuccess.vue';
import EToroConnectOtsAccount from './components/ots/etoro/EToroConnectOtsAccount.vue';
import eToroSuccess from './components/ots/etoro/eToroSuccess.vue';
import SalaryFinanceConnectOtsAccount from './components/ots/salary-finance/SalaryFinanceConnectOtsAccount.vue';
import SalaryFinanceSuccess from './components/ots/salary-finance/SalaryFinanceSuccess.vue';
import SyndesiConnectOtsAccount from './components/ots/syndesi/SyndesiConnectOtsAccount.vue';
import SyndesiSuccess from './components/ots/syndesi/SyndesiSuccess.vue';
import RiseupWLConnectOtsAccount from './components/ots/riseup-wl/RiseupWLConnectOtsAccount.vue';
import RiseupWLSuccess from './components/ots/riseup-wl/RiseupWLSuccess.vue';
import DescopeIlLogin from './components/descope/il/DescopeIlLogin.vue';
import config from './config';
import { fetchLatestJobByFlowId, setOtsFlowConfig, setOtsJobConfig } from './services/otsConfigService';
import { useSessionStore } from './stores/session';
import { FlowConfigIdType, WhiteLabelFirmIdType } from './types/otsConfig';
import { _descopeAutheticate } from './utils/descopeUtils';

const COOKIE_EXPIRATION_DAYS = 7;

const routes = [
  {
    path: '/',
    name: 'UnAuthenticatedRoutes',
    component: ErrorBoundary,
    beforeEnter: gretaBeforeEnter,
    children: [
      {
        path: 'il/login',
        component: DescopeIlLogin,
      },
      {
        path: 'login/mobile',
        component: DescopeMobileLogin,
      },
      {
        path: 'login',
        component: DescopeLogin,
      },
      {
        path: 'logout',
        component: DescopeLogout,
      },
      {
        path: 'signup',
        component: DescopeSignup,
      },
      {
        path: 'ots-signup',
        component: OtsStorySignup,
        beforeEnter: (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next:NavigationGuardNext,
        ) =>
          checkSignupQueryParams(to, from, next),
      },
      {
        path: 'etoro/signup',
        component: DescopeEtoroOtsSignup,
      },
      {
        path: 'salary-finance/signup',
        component: DescopeSalaryFinanceOtsSignup,
      },
      {
        path: 'riseup-wl/signup',
        component: DescopeRiseupWLOtsSignup,
      },
      {
        path: 'syndesi/signup',
        component: DescopeSyndesiOtsSignup,
      },
      {
        path: 'syndesi-wl/signup',
        component: DescopeSyndesiOtsSignup,
        meta: {
          whiteLabelFirmId: WhiteLabelFirmIdType.DefaultFirm,
        },
      },
      {
        path: 'prograd/signup',
        component: DescopeProgradOtsSignup,
      },
      {
        path: 'invite',
        component: DescopeMemberInvite,
      },
      {
        path: 'welcome-join',
        component: WelcomeNewMember,
      },
      {
        path: 'join-riseup',
        component: JoinRiseup,
      },
      {
        path: 'start-trial',
        component: ReferToDownloadApp,
        props: { title: 'Your 7 day free trial starts now.', text: 'Want to find out your monthly forecast? Download the app now.' },
      },
      {
        path: 'test/login',
        component: DescopeTestLogin,
      },
      {
        path: 'test/signup',
        component: DescopeTestSignup,
      },
      {
        path: 'test/mobile',
        component: DescopeMobileTestLogin,
      },
      {
        path: 'redirect',
        component: GoToApp,
        props: { title: 'Success!', text: 'You have successfully logged in, and can now continue to the app.' },
      },
      {
        path: '/ots',
        name: 'AuthenticatedRouts',
        component: AsyncLoadingWrapper,
        beforeEnter: _verifyOtsLogin,
        children: [
          {
            path: 'connect',
            component: OtsStoryConnect,
          },
          {
            path: 'success',
            component: OtsStorySuccess,
          },
          {
            path: 'vanilla',
            name: 'VanillaRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.Vanilla),
            children: [
              {
                path: 'success',
                component: OtsVanillaSuccess,
              },
            ],
          },
          {
            path: 'cstm',
            name: 'CustomRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.Custom),
            children: [
              {
                path: 'success',
                component: OtsCustomSuccess,
              },
            ],
          },
          {
            path: 'etoro',
            name: 'EtoroRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.Etoro),
            children: [
              {
                path: 'connect',
                component: EToroConnectOtsAccount,
              },
              {
                path: 'success',
                component: eToroSuccess,
              },
            ],
          },
          {
            path: 'salary-finance',
            name: 'SalaryFinanceRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.SalaryFinance),
            children: [
              {
                path: 'connect',
                component: SalaryFinanceConnectOtsAccount,
              },
              {
                path: 'success',
                component: SalaryFinanceSuccess,
              },
            ],
          },
          {
            path: 'riseup-wl',
            name: 'RiseupWLRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.RiseupWL),
            children: [
              {
                path: 'connect',
                component: RiseupWLConnectOtsAccount,
              },
              {
                path: 'success',
                component: RiseupWLSuccess,
              },
            ],
          },
          {
            path: 'syndesi',
            name: 'SyndesiRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.Syndesi),
            children: [
              {
                path: 'connect',
                component: SyndesiConnectOtsAccount,
              },
              {
                path: 'success',
                component: SyndesiSuccess,
              },
            ],
          },
          {
            path: 'syndesi-wl',
            name: 'SyndesiWlRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.Syndesi),
            children: [
              {
                path: 'connect',
                component: SyndesiConnectOtsAccount,
                meta: {
                  whiteLabelFirmId: WhiteLabelFirmIdType.DefaultFirm,
                },
              },
              {
                path: 'success',
                component: SyndesiSuccess,
                meta: {
                  whiteLabelFirmId: WhiteLabelFirmIdType.DefaultFirm,
                },
              },
            ],
          },
          {
            path: 'prograd',
            name: 'ProgradRoutes',
            component: AsyncLoadingWrapper,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next:NavigationGuardNext,
            ) =>
              _verifyOtsConfig(to, from, next, FlowConfigIdType.Prograd),
            children: [
              {
                path: 'connect',
                component: ProgradConnectOtsAccount,
              },
              {
                path: 'success',
                component: ProgradSuccess,
              },
            ],
          },
        ],
      },
    ],
  },
];
async function checkSignupQueryParams(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  const flowConfigId = to.query.fid || to.query.flowConfigId || to.query.utm_variant;
  const sessionStore = useSessionStore();
  await sessionStore.fetchOtsFlowConfig(flowConfigId as string);

  if (flowConfigId === FlowConfigIdType.Etoro) {
    router.push({ path: '/etoro/signup', query: to.query });
  }
  if (flowConfigId === FlowConfigIdType.SalaryFinance) {
    router.push({ path: '/salary-finance/signup', query: to.query });
  }
  if (flowConfigId === FlowConfigIdType.Syndesi) {
    router.push({ path: '/syndesi/signup', query: to.query });
  }
  if (flowConfigId === FlowConfigIdType.RiseupWL) {
    router.push({ path: '/riseup-wl/signup', query: to.query });
  }
  return next();
}

async function _verifyOtsConfig(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  expectedUtmVariant: FlowConfigIdType,
): Promise<void> {
  const isAuthenticated = await _isAuthenticated();
  const sessionStore = useSessionStore();
  await sessionStore.fetchAllowedOtsConfigs();

  // check query for jobId and if present - setjob id in otsJob config and init config object
  const { jobId } = to.query;
  if (jobId) {
    setOtsJobConfig(expectedUtmVariant, jobId as string);
  }

  if (isAuthenticated) {
    return next();
  }
  router.push({ path: '/ots-signup', query: { ...to.query } });

  return next();
}

async function _verifyOtsLogin(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> {
  const { token } = to.query;
  if (token && typeof token === 'string') {
    // eslint-disable-next-line no-param-reassign
    delete to.query.token;
    const isDescopeAutheticated = await _descopeAutheticate(token);
    if (isDescopeAutheticated) {
      await _initSession(to.query.fid as string);
      return next();
    }
  }
  const isAuthenticated = await _isAuthenticated();
  if (isAuthenticated) {
    return next();
  }

  router.push({ path: '/ots-signup', query: { ...to.query } });

  return next();
}

async function _isAuthenticated(): Promise<boolean> {
  const loggedIn = await isLoggedIn();
  if (loggedIn) {
    await _initSession();
    return true;
  }

  return false;
}

async function _initSession(fid?: string) {
  const sessionStore = useSessionStore();
  await sessionStore.init();
  if (fid) {
    const otsFlowConfig = await sessionStore.fetchOtsFlowConfig(fid);
    setOtsFlowConfig(otsFlowConfig);
    await fetchLatestJobByFlowId(fid);
  }
  if (sessionStore.customerId && sessionStore.memberId) {
    Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);
  }
}

function gretaBeforeEnter() {
  const gclid = new URLSearchParams(window.location.search).get('gclid');
  if (gclid) {
    DDLogs.info('User came with gclid', { gclid });
    const { hostname } = new URL(config.get().gretaPublicUrl);
    const domain = `.${hostname.split('.').slice(1).join('.')}`;
    Cookies.set('gclid', gclid, { expires: COOKIE_EXPIRATION_DAYS, domain, path: '/' });
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});
export default router;
